import React, { useEffect } from 'react';
import './App.css';
import './styles/v2.css';
import { profile, experience, skills, education, certifications, testimonials, consulting } from './content';
import Hero from './components/Hero';
import About from './components/About';
import Experience from './components/Experience';
import Skills3D from './components/Skills3D';
import Education3D from './components/Education3D';
import Testimonials from './components/Testimonials';
import Consulting from './components/Consulting';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ConsentBanner from './components/ConsentBanner';

function App() {
  useEffect(() => {
    // v2-root is set statically in index.html to avoid a flash of the base
    // theme on first paint; we just ensure it's present here.
    document.documentElement.style.scrollBehavior = 'smooth';
    document.documentElement.classList.add('v2-root');
  }, []);

  // Scroll-reveal for all section titles
  useEffect(() => {
    const titles = Array.from(document.querySelectorAll('.section-title'));
    if (titles.length === 0) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      titles.forEach((t) => t.classList.add('v2-title-reveal', 'is-revealed'));
      return undefined;
    }

    titles.forEach((t) => t.classList.add('v2-title-reveal'));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -8% 0px' }
    );
    titles.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Top scroll-progress bar
  useEffect(() => {
    const bar = document.getElementById('v2-scroll-progress');
    if (!bar) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const p = max > 0 ? Math.min(1, el.scrollTop / max) : 0;
      bar.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="App" id="home">
      <div id="v2-scroll-progress" className="v2-scroll-progress" aria-hidden="true" />

      {/* Story 1 — Intro: identity ↔ about */}
      <section className="v2-story v2-story--intro">
        <div className="v2-story-col v2-story-col--left">
          <Hero data={profile} />
        </div>
        <div className="v2-story-col v2-story-col--right">
          <About data={profile.about} />
        </div>
      </section>

      {/* Story 2 — Journey: experience ↔ education */}
      <section className="v2-story v2-story--journey">
        <div className="v2-story-grid">
          <div className="v2-story-col v2-story-col--left">
            <Experience data={experience} />
          </div>
          <div className="v2-story-col v2-story-col--right">
            <Education3D education={education} certifications={certifications} />
          </div>
        </div>
      </section>

      {/* Story 3 — Skills & Tools (full width) */}
      <section className="v2-story v2-story--skills">
        <Skills3D data={skills} />
      </section>

      {/* What People Say — full-width, centered */}
      <section className="v2-story v2-story--testimonials">
        <Testimonials data={testimonials} />
      </section>

      {/* Schedule a meeting — centered */}
      <Contact data={profile} variant="calendar" />

      {/* Connect — Consulting (left) · Get in touch (right) */}
      <section className="v2-story v2-story--connect">
        <div className="v2-story-grid">
          <div className="v2-story-col v2-story-col--left">
            <Consulting data={consulting} email={profile.email} />
          </div>
          <div className="v2-story-col v2-story-col--right">
            <Contact data={profile} variant="form" />
          </div>
        </div>
      </section>

      <Footer />
      <ConsentBanner />
    </div>
  );
}

export default App;
